import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import Sitemap from "routes/sitemap";
import { applyOnboardingParam, getOnboardingContent, isOnboardingPostponed, isOnboardingSeen, markOnboardingSeen, ONBOARDING_PARAM, ONBOARDING_START_STATE, postponeOnboarding } from "helpers/onboarding";
import classes from "./Onboarding.module.scss";

/** Gives the header/side bar time to render before the welcome dialog appears. */
const WELCOME_DELAY_MS = 800;

/** Same idea when the tour is started on request, after navigating to the home page. */
const TOUR_START_DELAY_MS = 400;

// Desktop and mobile layouts render separate copies of some elements (e.g. receivables),
// so each tour step targets whichever copy is actually on screen.
const isVisible = (element) => {
    if (element.checkVisibility && !element.checkVisibility({ opacityProperty: true, visibilityProperty: true })) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
};

const findTarget = (id) => [...document.querySelectorAll(`[data-tour="${id}"]`)].find(isVisible);

/**
 * Welcome dialog and guided tour for new users.
 *
 * Steps point at elements marked with data-tour="<step id>"; steps whose element isn't
 * visible (e.g. side bar links on mobile) are skipped.
 */
const Onboarding = () => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [showWelcome, setShowWelcome] = useState(false);
    const tourRef = useRef(null);
    const startRef = useRef(null);

    const content = getOnboardingContent(i18n.language);
    const param = searchParams.get(ONBOARDING_PARAM);

    useEffect(() => {
        if (applyOnboardingParam(searchParams)) {
            const next = new URLSearchParams(searchParams);
            next.delete(ONBOARDING_PARAM);
            setSearchParams(next, { replace: true });
        }

        if (tourRef.current || isOnboardingSeen() || isOnboardingPostponed()) {
            setShowWelcome(false);
            return;
        }

        const timer = setTimeout(() => setShowWelcome(true), WELCOME_DELAY_MS);
        return () => clearTimeout(timer);
        // Re-evaluate only when the override param changes, not on every navigation.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [param]);

    useEffect(() => {
        if (!showWelcome) return;
        const previousFocus = document.activeElement;
        const closeOnEscape = (event) => {
            if (event.key !== "Escape") return;
            postponeOnboarding();
            setShowWelcome(false);
        };

        startRef.current?.focus();
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("keydown", closeOnEscape);
            previousFocus?.focus?.();
        };
    }, [showWelcome]);

    // Close the tour if the user logs out mid-way
    useEffect(() => () => tourRef.current?.destroy(), []);

    // Started from elsewhere (manual page button), see startOnboardingTour()
    const startRequested = location.state?.[ONBOARDING_START_STATE] === true;
    useEffect(() => {
        if (!startRequested) return;

        const timer = setTimeout(() => {
            if (!tourRef.current) startTourRef.current();
            // Drop the flag so a refresh or back/forward doesn't start the tour again
            navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
        }, TOUR_START_DELAY_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startRequested]);

    const postpone = () => {
        postponeOnboarding();
        setShowWelcome(false);
    };

    const dismiss = () => {
        markOnboardingSeen();
        setShowWelcome(false);
    };

    const startTour = () => {
        setShowWelcome(false);
        const { buttons, finish } = content;

        const steps = content.steps
            .filter((step) => findTarget(step.id))
            .map((step) => ({
                element: () => findTarget(step.id),
                popover: { title: step.title, description: step.description },
            }));

        steps.push({
            popover: {
                title: finish.title,
                description: finish.description,
                onPopoverRender: (popover) => {
                    const manualButton = document.createElement("button");
                    manualButton.type = "button";
                    manualButton.className = "b2b-tour-manual";
                    manualButton.textContent = finish.manual;
                    manualButton.addEventListener("click", () => {
                        tourRef.current?.destroy();
                        navigate(Sitemap.static.manual);
                    });
                    // Own row under the text, so the footer keeps just progress + prev/done
                    popover.description.appendChild(manualButton);
                },
            },
        });

        const tour = driver({
            steps,
            showProgress: true,
            progressText: buttons.progress,
            nextBtnText: buttons.next,
            prevBtnText: buttons.prev,
            doneBtnText: buttons.done,
            closeBtnLabel: buttons.close,
            popoverClass: "b2b-tour",
            stageRadius: 6,
            overlayClickBehavior: "none",
            // Clicking a highlighted link would navigate away mid-tour
            disableActiveInteraction: true,
            // Finished, closed or escaped: any ending counts as seen
            onDestroyed: () => {
                markOnboardingSeen();
                tourRef.current = null;
            },
        });

        tourRef.current = tour;
        tour.drive();
    };

    // The event listener is registered once, so it calls the latest startTour (current language)
    const startTourRef = useRef(startTour);
    startTourRef.current = startTour;

    if (!showWelcome) return null;

    return (
        <div
            className={classes.backdrop}
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) postpone();
            }}
        >
            <div className={classes.dialog} role="dialog" aria-modal="true" aria-labelledby="onboarding-title" aria-describedby="onboarding-text">
                <button type="button" className={classes.close} onClick={postpone} aria-label={content.welcome.close}>
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M5 5 15 15M15 5 5 15" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    </svg>
                </button>
                <h2 id="onboarding-title" className={classes.title}>
                    {content.welcome.title}
                </h2>
                <p id="onboarding-text" className={classes.text}>
                    {content.welcome.text}
                </p>
                <div className={classes.actions}>
                    <button ref={startRef} type="button" className={classes.primary} onClick={startTour}>
                        {content.welcome.start}
                    </button>
                    <button type="button" className={classes.secondary} onClick={postpone}>
                        {content.welcome.later}
                    </button>
                </div>
                <button type="button" className={classes.dismiss} onClick={dismiss}>
                    {content.welcome.dismiss}
                </button>
            </div>
        </div>
    );
};

export default Onboarding;
